import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule
  ],
  template: `
    <div class="login-page">
      <!-- Panel de formulario (izquierda, fondo blanco) -->
      <div class="form-panel">
        <div class="form-card">
          <div class="form-header">
            <h2>Bienvenido de nuevo</h2>
            <p>Inicia sesión para acceder a tu panel</p>
          </div>

          <form [formGroup]="loginForm" (ngSubmit)="onSubmit()">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Correo electrónico</mat-label>
              <input matInput formControlName="email" type="email" required autocomplete="email" autofocus>
              <mat-icon matPrefix>email</mat-icon>
              <mat-error *ngIf="loginForm.get('email')?.hasError('required')">
                El correo electrónico es requerido
              </mat-error>
              <mat-error *ngIf="loginForm.get('email')?.hasError('email')">
                Ingrese un correo electrónico válido
              </mat-error>
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Contraseña</mat-label>
              <input matInput [type]="hidePassword ? 'password' : 'text'" formControlName="password" required autocomplete="current-password">
              <mat-icon matPrefix>lock</mat-icon>
              <button type="button" mat-icon-button matSuffix (click)="hidePassword = !hidePassword" [attr.aria-label]="'Hide password'">
                <mat-icon>{{hidePassword ? 'visibility_off' : 'visibility'}}</mat-icon>
              </button>
              <mat-error *ngIf="loginForm.get('password')?.hasError('required')">
                La contraseña es requerida
              </mat-error>
            </mat-form-field>

            <button mat-raised-button color="primary" class="submit-btn" type="submit" [disabled]="loginForm.invalid || isLoading">
              <span *ngIf="!isLoading">Iniciar Sesión</span>
              <span *ngIf="isLoading">Iniciando...</span>
            </button>
          </form>

          <div class="form-footer">
            <span>¿No tienes cuenta?</span>
            <a routerLink="/register">Regístrate</a>
          </div>
        </div>
      </div>

      <!-- Panel de marca (derecha, fondo rojo) -->
      <div class="brand-panel">
        <div class="brand-content">
          <div class="brand-logo">
            <img src="/logo.png" alt="SecAudit" />
          </div>
          <h1 class="brand-title">SecAudit</h1>
          <p class="brand-tagline">Auditoría de seguridad web inteligente</p>

          <div class="brand-features">
            <div class="brand-feature">
              <mat-icon>shield</mat-icon>
              <div>
                <strong>Detección de vulnerabilidades</strong>
                <span>Escaneo continuo de tus sitios</span>
              </div>
            </div>
            <div class="brand-feature">
              <mat-icon>description</mat-icon>
              <div>
                <strong>Reportes profesionales</strong>
                <span>Hallazgos y recomendaciones claras</span>
              </div>
            </div>
            <div class="brand-feature">
              <mat-icon>notifications_active</mat-icon>
              <div>
                <strong>Alertas en tiempo real</strong>
                <span>Actúa antes de que sea tarde</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .login-page {
      min-height: 100vh;
      display: flex;
      background: var(--surface-color);
    }

    /* Panel de marca (derecha, fondo dark con glow rojo) */
    .brand-panel {
      flex: 1;
      background:
        radial-gradient(ellipse 80% 60% at 75% 15%, rgba(183, 28, 28, 0.35), transparent 60%),
        radial-gradient(ellipse 55% 45% at 15% 85%, rgba(183, 28, 28, 0.18), transparent 65%),
        var(--surface-dark);
      color: var(--text-on-dark);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 48px;
      position: relative;
      overflow: hidden;
    }
    .brand-panel::before {
      content: '';
      position: absolute;
      inset: 0;
      background-image:
        linear-gradient(rgba(255, 255, 255, 0.035) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px);
      background-size: 48px 48px;
      mask-image: radial-gradient(ellipse 85% 80% at 50% 35%, #000 25%, transparent 85%);
      -webkit-mask-image: radial-gradient(ellipse 85% 80% at 50% 35%, #000 25%, transparent 85%);
      pointer-events: none;
    }
    .brand-panel::after {
      content: '';
      position: absolute;
      top: -15%;
      right: -15%;
      width: 55%;
      height: 55%;
      border-radius: 50%;
      background: rgba(183, 28, 28, 0.12);
      filter: blur(40px);
      pointer-events: none;
    }
    .brand-content {
      max-width: 420px;
      position: relative;
      z-index: 1;
    }
    .brand-logo {
      width: 80px;
      height: 80px;
      border-radius: 18px;
      background: #ffffff;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 24px;
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.35), 0 0 48px rgba(183, 28, 28, 0.25);
    }
    .brand-logo img {
      width: 56px;
      height: 56px;
      object-fit: contain;
    }
    .brand-title {
      font-family: var(--font-display);
      font-size: 40px;
      font-weight: 800;
      margin: 0 0 8px 0;
      letter-spacing: -0.03em;
    }
    .brand-tagline {
      font-size: 16px;
      color: var(--text-muted-on-dark);
      margin: 0 0 40px 0;
    }
    .brand-features {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }
    .brand-feature {
      display: flex;
      align-items: center;
      gap: 14px;
    }
    .brand-feature mat-icon {
      font-size: 22px;
      width: 44px;
      height: 44px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 12px;
      background: rgba(183, 28, 28, 0.18);
      color: var(--text-soft-on-dark);
      flex-shrink: 0;
    }
    .brand-feature strong {
      display: block;
      font-size: 15px;
      font-weight: 600;
    }
    .brand-feature span {
      display: block;
      font-size: 13px;
      color: var(--text-muted-on-dark);
      margin-top: 2px;
    }

    /* Panel de formulario (izquierda, fondo blanco) */
    .form-panel {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 32px;
      background: var(--surface-color);
    }
    .form-card {
      width: 100%;
      max-width: 400px;
    }
    .form-header {
      margin-bottom: 32px;
    }
    .form-header h2 {
      font-family: var(--font-display);
      font-size: 30px;
      font-weight: 800;
      letter-spacing: -0.03em;
      color: var(--text-primary);
      margin: 0 0 8px 0;
    }
    .form-header p {
      font-size: 15px;
      color: var(--text-secondary);
      margin: 0;
    }

    form {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
    .full-width {
      width: 100%;
    }
    .submit-btn {
      width: 100%;
      height: 50px;
      font-size: 15px;
      font-weight: 600;
      margin-top: 8px;
      border-radius: 10px;
      box-shadow: 0 4px 14px rgba(183, 28, 28, 0.25);
    }
    .form-footer {
      margin-top: 24px;
      text-align: center;
      font-size: 14px;
      color: var(--text-secondary);
    }
    .form-footer a {
      color: var(--primary-color);
      font-weight: 600;
      text-decoration: none;
      margin-left: 4px;
    }
    .form-footer a:hover {
      text-decoration: underline;
    }

    mat-form-field {
      ::ng-deep .mdc-text-field--outlined {
        border-radius: 10px;
      }
    }

    /* Responsive */
    @media (max-width: 900px) {
      .brand-panel {
        display: none;
      }
      .form-panel {
        flex: 1;
      }
    }
  `]
})
export class LoginComponent {
  loginForm: FormGroup;
  hidePassword = true;
  isLoading = false;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private snackBar: MatSnackBar,
    private route: ActivatedRoute
  ) {
    const rememberedEmail = localStorage.getItem('remembered_email') || '';
    this.loginForm = this.fb.group({
      email: [rememberedEmail, [Validators.required, Validators.email]],
      password: ['', Validators.required]
    });

    if (this.authService.isAuthenticated()) {
      this.router.navigate([this.authService.getHomeRoute()]);
    }

    this.route.queryParams.subscribe(params => {
      if (params['sessionExpired']) {
        this.snackBar.open('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.', 'Cerrar', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['warning-snackbar']
        });
      }
    });
  }

  onSubmit(): void {
    if (this.loginForm.invalid) {
      return;
    }

    this.isLoading = true;

    const loginRequest = this.loginForm.value;

    this.authService.login(loginRequest).subscribe({
      next: (response) => {
        this.isLoading = false;
        localStorage.setItem('remembered_email', response.email);
        this.snackBar.open(`¡Bienvenido, ${response.username}!`, 'Cerrar', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top'
        });
        this.router.navigate([this.authService.getHomeRoute()]);
      },
      error: (error) => {
        this.isLoading = false;
        const errorMessage = error.error?.message || 'Error al iniciar sesión';
        this.snackBar.open(errorMessage, 'Cerrar', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
      }
    });
  }
}
